import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import dayjs from "dayjs";
import { api } from "../classCountApi";
import { stringify } from "../../../../utils/stringify";
import { API_DATE_FORMAT } from "../../../../app_constants";

export const SET_LOADING = "sagas/attendance/classCount/SET_LOADING";
export const SET_UPDATES_LOADING = "sagas/attendance/classCount/SET_UPDATES_LOADING";

export const SET_DYNAMIC_FILTERS_STATUS = "sagas/attendance/classCount/SET_DYNAMIC_FILTERS_STATUS";

export const SET_FIRST_EDITED_DYNAMIC_FILTER =
  "sagas/attendance/classCount/SET_FIRST_EDITED_DYNAMIC_FILTER";

export const RESET_DYNAMIC_FILTERS_STATUS =
  "sagas/attendance/classCount/RESET_DYNAMIC_FILTERS_STATUS";

export const SET_FILTERS_DIRTY = "sagas/attendance/classCount/SET_FILTERS_DIRTY";
export const FETCH_CURRENT_USER = "sagas/attendance/classCount/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "sagas/attendance/classCount/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_CHART_SUCCESS = "sagas/attendance/classCount/FETCH_CHART_SUCCESS";

export const FETCH_FILTERS_SUCCESS = "sagas/attendance/classCount/FETCH_FILTERS_SUCCESS";
export const REFETCH_FILTERS_SUCCESS = "sagas/attendance/classCount/REFETCH_FILTERS_SUCCESS";
export const UPDATE_CHART = "sagas/attendance/classCount/UPDATE_CHART";

export const SET_DATES = "sagas/attendance/classCount/SET_DATES";

export const UPDATE_DATES = "sagas/attendance/classCount/UPDATE_DATES";

const RESET_FILTER = "sagas/attendance/classCount/RESET_FILTER";
export const RESET_FILTER_SELECTED = "sagas/attendance/classCount/RESET_FILTER_SELECTED";
export const UPDATE_CUSTOM_RANGE = "sagas/attendance/classCount/UPDATE_CUSTOM_RANGE";

export const UPDATE_FILTERS = "sagas/attendance/classCount/UPDATE_FILTERS";
export const UPDATE_TABLE_VIEW = "sagas/attendance/classCount/UPDATE_TABLE_VIEW";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateData: createAction(UPDATE_CHART),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  resetFilters: createAction(RESET_FILTER),
  updateFilters: createAction(UPDATE_FILTERS),
  updateTableView: createAction(UPDATE_TABLE_VIEW)
};

export default function* watchClassCount() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_CHART, updateData);
  yield takeLatest(RESET_FILTER, resetFilters);
  yield takeLatest(UPDATE_DATES, refetchFilters);
  yield takeLatest(UPDATE_CUSTOM_RANGE, refetchFilters);
  yield takeLatest(UPDATE_FILTERS, refetchDynamicFilters);
  yield takeLatest(UPDATE_TABLE_VIEW, updateTableView);
}

function* updateTableView() {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield fetchData();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* setDates() {
  const maxDate = yield select((state) => state.classCount.currentUser.maxDate);
  const startDate = dayjs(maxDate).startOf("month").subtract(3, "months").format(API_DATE_FORMAT);

  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate: maxDate
    }
  });
}

function* fetchData() {
  const commonParams = yield getCommonParams();
  const params = yield getApiParams();

  try {
    const { data } = yield call(api.getChart, { ...commonParams, ...params });

    yield put({ type: FETCH_CHART_SUCCESS, payload: data });
  } catch (err) {
    console.error(err);
  }
}

function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: {
      ...data.payload,
      maxDate: dayjs(data.payload.calendarEndDate).add(2, "months").format(API_DATE_FORMAT)
    }
  });

  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* fetchFilters() {
  const params = yield getCommonParams();

  try {
    const { data } = yield call(api.getFilters, params);

    yield put({ type: FETCH_FILTERS_SUCCESS, payload: data });
  } catch (error) {
    console.error(error);
  }
}

function* refetchFilters() {
  const params = yield getCommonParams();

  try {
    yield put({ type: SET_DYNAMIC_FILTERS_STATUS });

    const filters = yield call(api.getFilters, {
      ...params
    });

    yield put({ type: REFETCH_FILTERS_SUCCESS, payload: filters.data });
    yield put({ type: RESET_DYNAMIC_FILTERS_STATUS });
  } catch (error) {
    console.error(error);
  }
}

const requestParams = {
  staff: "staff_ids",
  class: "class_ids"
};

function* checkForFirstFilterControl(currentFilterName, data) {
  //first changed dynamic filter makes other dynamic filters static, meaning no request send to backend
  let firstEditedDynamicFilter = yield select((state) => state.classCount.firstEditedDynamicFilter);

  if (!firstEditedDynamicFilter) {
    yield put({ type: SET_FIRST_EDITED_DYNAMIC_FILTER, payload: currentFilterName });
  }

  if (firstEditedDynamicFilter && currentFilterName === firstEditedDynamicFilter) {
    let allOptions = yield select((state) => state.classCount.filters[firstEditedDynamicFilter]);

    //if all options were selected, then  remove first filter control
    if (allOptions.length === data.length) {
      yield put({ type: SET_FIRST_EDITED_DYNAMIC_FILTER, payload: "" });
    }
  }

  return firstEditedDynamicFilter && currentFilterName !== firstEditedDynamicFilter;
}

function* refetchDynamicFilters(info) {
  let { key, data } = info.payload;

  const params = yield getCommonParams();
  let skipRefetch = yield checkForFirstFilterControl(key, data);

  if (skipRefetch) return;

  try {
    yield put({ type: SET_DYNAMIC_FILTERS_STATUS, payload: key });

    const res = yield call(api.getFilters, {
      ...params,
      [requestParams[key]]: stringify(data)
    });

    yield put({ type: REFETCH_FILTERS_SUCCESS, payload: res.data });
    yield put({ type: RESET_DYNAMIC_FILTERS_STATUS });
  } catch (error) {
    console.error(error);
  }
}

function* updateData() {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield fetchData();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_LOADING, payload: true });
  yield setDates();
  yield put({ type: RESET_FILTER_SELECTED });
  try {
    yield fetchFilters();
    yield fetchData();
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* getCommonParams() {
  let { short_url } = yield select((state) => state.commonReducer.commonProps);

  let { startDate, endDate } = yield select((state) => state.classCount.selectedRequestDates);

  return {
    short_url,
    start_date: startDate,
    end_date: endDate
  };
}

function* getApiParams() {
  return yield select((state) => {
    let { staff, class: class_ids, table_view } = state.classCount.selectedFilters;
    return { staff_ids: stringify(staff), class_ids: stringify(class_ids), table_view };
  });
}
